import React, { useState } from 'react';
import Papa from 'papaparse';
import { readExcelFileAsRows } from '../utils/excel';
import { useSelector } from 'react-redux';

const apiPrefix = process.env.REACT_APP_API_PREFIX;

const ImportCsvExcel = () => {
    const [data, setData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(1); // Commence à 1 pour ignorer l'en-tête CSV
    const token = useSelector((state) => state.user.token);

    const hide = () => {
        setShowModal(false);
        setData([]);
        window.location.reload();
    };

    const sanitizeInput = (input) => {
        if (typeof input !== 'string') return input; // Si ce n'est pas une chaîne, retourne tel quel.
    
        const replacements = {
            "'": ' ',  // Remplace les apostrophes par des espaces
            "é": 'e',
            "è": 'e',
            "ê": 'e',
            "à": 'a',
            "ù": 'u',
            "ç": 'c',
            "ö": 'o',
            "î": 'i',
        };
    
        return input.replace(/./g, (char) => replacements[char] || char); // Applique les remplacements
    };
    
    

    const cleanData = (data) => {
        return data.map(row =>
            row.map(cell => {
                if (typeof cell === 'string') {
                    const normalizedCell = cell.normalize("NFC"); // Combine les caractères accentués
                    return sanitizeInput(normalizedCell); // Nettoie les caractères spéciaux
                }
                return cell;
            })
        );
    };
    

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        const reader = new FileReader();
    
        reader.onload = async (evt) => {
            let fileContent;
    
            if (file.type === 'text/csv' || file.type === 'application/vnd.ms-excel') {
                const textDecoder = new TextDecoder("utf-8");
                fileContent = textDecoder.decode(new Uint8Array(evt.target.result));
            } else {
                fileContent = evt.target.result;
            }
    
            if (file.type === 'text/csv') {
                Papa.parse(fileContent, {
                    complete: (results) => {
                        const nonEmptyRows = results.data.filter(row => row.some(item => item));
                        const cleanedData = cleanData(nonEmptyRows); // Applique la normalisation
                        setData(oldData => [...oldData, ...cleanedData]);
                    },
                    skipEmptyLines: true,
                    encoding: "UTF-8",
                });
            } else if (file.type.includes('spreadsheetml') || file.name.endsWith('.xlsx')) {
                const jsonData = await readExcelFileAsRows(file);
                const nonEmptyRows = jsonData.filter(row => row.some(item => item));
                const cleanedData = cleanData(nonEmptyRows);
                setData(oldData => [...oldData, ...cleanedData]);
            }
        };
    
        reader.readAsArrayBuffer(file);
    };
    

    const handleRegisterUsers = async (startIndex = 1) => {
        for (let i = startIndex; i < data.length; i++) {
            const row = data[i];
            const [username, password, firstname, lastname, company, siret, chefEntreprise, active] = row;

            const formData = {
                username,
                password,
                firstname: sanitizeInput(firstname),
                lastname: sanitizeInput(lastname),
                company: sanitizeInput(company),
                siret,
                chefEntreprise: chefEntreprise === 'true' || chefEntreprise === true,
                active: active === 'true' || active === true,
            };

            try {
                const response = await fetch(apiPrefix + 'register', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData),
                });

                if (!response.ok) {
                    const errorMessage = await response.json();
                    if (response.status === 502) {
                        setCurrentUser(formData); // Enregistre les données de l'utilisateur pour confirmation
                        setShowConfirmModal(true); // Affiche la modale de confirmation
                        setCurrentIndex(i + 1); // Enregistre l'index du prochain utilisateur
                        return; // Interrompt la boucle pour attendre la confirmation
                    } else {
                        console.error('Erreur lors de l\'inscription:', errorMessage.error);
                    }
                } else {
                    console.log('Utilisateur inscrit avec succès');
                }
            } catch (error) {
                console.error('Erreur lors de la tentative d\'inscription:', error);
            }
        }

        setShowModal(true); // Affiche la confirmation finale une fois la boucle terminée
    };

    const handleConfirmUpdate = async (confirm) => {
        if (confirm && currentUser) {

            const sanitizedUser = {
                ...currentUser,
                firstname: sanitizeInput(currentUser.firstname),
                lastname: sanitizeInput(currentUser.lastname),
                company: sanitizeInput(currentUser.company),
            };

            try {
                const response = await fetch(apiPrefix + 'admin/updateimport', {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(sanitizedUser),
                });

                if (response.ok) {
                    console.log('Utilisateur mis à jour avec succès.');
                } else {
                    const errorMessage = await response.json();
                    console.error('Erreur lors de la mise à jour de l\'utilisateur:', errorMessage.error);
                }
            } catch (error) {
                console.error('Erreur lors de la mise à jour:', error);
            }
        }

        setCurrentUser(null);
        setShowConfirmModal(false);
        handleRegisterUsers(currentIndex); // Reprend la boucle à partir du prochain utilisateur
    };

    return (
        <div className="card mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                <i className="fas fa-file-import mr-2 text-primary"></i>
                Import CSV/Excel
            </h3>
            
            <div className="form-group">
                <label className="form-label">
                    <i className="fas fa-file mr-2.5 text-primary"></i>
                    Sélectionner un fichier
                </label>
                <input 
                    type="file" 
                    accept=".csv, .xlsx" 
                    onChange={handleFileUpload}
                    className="form-input"
                />
                <p className="text-sm text-gray-500 mt-1">Formats acceptés: CSV, Excel (.xlsx)</p>
            </div>
            
            {data.length > 0 && (
                <div className="mt-6">
                    <button 
                        onClick={() => handleRegisterUsers(1)}
                        className="btn-primary mb-4"
                    >
                        <i className="fas fa-user-plus mr-2"></i>
                        Inscrire les utilisateurs
                    </button>
                    
                    <div className="card">
                        <h4 className="text-md font-semibold text-gray-800 mb-4">Aperçu des données</h4>
                        <div className="overflow-x-auto">
                            <table className="table">
                                <tbody>
                                    {data.map((row, rowIndex) => (
                                        <tr key={rowIndex}>
                                            {row.map((item, itemIndex) => (
                                                <td key={itemIndex} className="text-sm">
                                                    {typeof item === 'string' ? sanitizeInput(item) : item}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            
            {showModal && (
                <div className="modal-overlay" onClick={hide}>
                    <div className="modal-content">
                        <button 
                            className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
                            onClick={hide}
                        >
                            ×
                        </button>
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-primary mb-4">Succès !</h2>
                            <p className="text-gray-700 mb-6">Inscription réussie !</p>
                            <button 
                                onClick={hide}
                                className="btn-primary"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {showConfirmModal && (
                <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
                    <div className="modal-content max-w-2xl">
                        <button 
                            className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
                            onClick={() => setShowConfirmModal(false)}
                        >
                            ×
                        </button>
                        <div className="p-4">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                <i className="fas fa-exclamation-triangle mr-2 text-yellow-500"></i>
                                Utilisateur existant
                            </h3>
                            <p className="text-gray-700 mb-4">
                                L'utilisateur suivant existe déjà. Voulez-vous mettre à jour ses informations avec les données suivantes ?
                            </p>
                            <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                <ul className="space-y-2 text-sm">
                                    <li><strong>Email:</strong> {currentUser?.username}</li>
                                    <li><strong>Mot de passe:</strong> {currentUser?.password}</li>
                                    <li><strong>Prénom:</strong> {currentUser?.firstname}</li>
                                    <li><strong>Nom:</strong> {currentUser?.lastname}</li>
                                    <li><strong>Entreprise:</strong> {currentUser?.company}</li>
                                    <li><strong>SIRET:</strong> {currentUser?.siret}</li>
                                    <li><strong>Chef d'Entreprise:</strong> {currentUser?.chefEntreprise ? 'Oui' : 'Non'}</li>
                                    <li><strong>Actif:</strong> {currentUser?.active ? 'Oui' : 'Non'}</li>
                                </ul>
                            </div>
                            <div className="flex gap-3 justify-center">
                                <button 
                                    onClick={() => handleConfirmUpdate(true)}
                                    className="btn-primary"
                                >
                                    <i className="fas fa-check mr-2"></i>
                                    Confirmer
                                </button>
                                <button 
                                    onClick={() => handleConfirmUpdate(false)}
                                    className="btn-secondary"
                                >
                                    <i className="fas fa-times mr-2"></i>
                                    Annuler
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImportCsvExcel;
